import type { SyncDeviceIdentity } from './syncElection';

/**
 * Base interface for syncable records.
 * All records have updatedAt (integer timestamp).
 */
export interface SyncableRecord {
  id: string;
  updatedAt?: number;
  updated_at?: number;
}

// Socket response types
export interface CreateRoomResponse {
  roomId?: string;
  error?: string;
}

export interface JoinRoomResponse {
  success?: boolean;
  error?: string;
}

// Callback types
export type MessageHandler = (message: SyncMessage) => void;
export type SyncConnectionState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'syncing'
  | 'completed'
  | 'error'
  | 'disconnected'
  | 'failed'
  | 'decrypt_failed';

export type StateChangeHandler = (state: SyncConnectionState) => void;
export type PeerJoinedHandler = () => void;

// Omit that distributes over a union, so each variant keeps its own
// type/data pairing instead of collapsing into one widened member.
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

// A message before the transport stamps it — same union, minus `timestamp`.
export type OutgoingSyncMessage = DistributiveOmit<SyncMessage, 'timestamp'>;

// Sync message types.
//
// Discriminated on `type`, so switching on it narrows `data` to exactly the
// payload that variant carries — no casts, and adding a new variant without
// handling it is a compile error.
export type SyncMessage = { timestamp: number } & (
  | { type: 'record_ids'; data: RecordIdsData[] }
  | { type: 'missing_records_response'; data: MissingRecordsResponseData }
  | { type: 'sync_complete'; data: SyncCompleteData }
  | { type: 'sync_error'; data: { error: string } }
  | { type: 'sync_request'; data: { tables: SyncTableName[] } }
  | { type: 'sync_confirmation'; data: SyncConfirmationData }
  | { type: 'blob_data'; data: BlobData }
  | { type: 'blob_chunk'; data: BlobChunkData }
  | { type: 'blob_chunk_complete'; data: BlobChunkCompleteData }
  | { type: 'passphrase_verify'; data?: undefined }
  | { type: 'hello'; data: SyncHelloData }
  | { type: 'tombstones'; data: SyncTombstonesData }
);

// Parts of the protocol a device may or may not speak. Announced in `hello`,
// and used only when both devices list them: a version from before a
// capability ignores the message types it adds and would never answer them.
export const SYNC_CAPABILITIES = {
  // Deletes travel as tombstones (the `tombstones` message).
  TOMBSTONES: 'tombstones'
} as const;

export type SyncCapability =
  (typeof SYNC_CAPABILITIES)[keyof typeof SYNC_CAPABILITIES];

// First message of a session: who this device is, so the pair can elect a
// primary (syncElection.ts) instead of asking the user for a direction.
export interface SyncHelloData extends SyncDeviceIdentity {
  /**
   * False when this device only hands its data over (web's "Transfer to
   * Mobile" deletes its copy afterwards), so the primary skips sending its own
   * records back.
   */
  wantsCopy: boolean;
  /**
   * What this device speaks beyond the base protocol. Absent on a version from
   * before capabilities, which is read as none.
   */
  capabilities?: SyncCapability[];
}

// Progress tracking
export type ProgressHandler = (progress: {
  current: number;
  total: number;
  stage: string;
  details: string;
}) => void;

export interface SyncStatistics {
  totalTables: number;
  processedTables: number;
  totalRecords: number;
  transferredRecords: number;
  startTime: number;
}

// Data types for sync
export interface RecordIdsData {
  table: string;
  records: Array<{
    id: string;
    updated_at: number;
  }>;
}

export interface MissingRecordsResponseData {
  table: string;
  records: DatabaseRecord[];
}

export interface SyncCompleteData {
  timestamp: number;
  totalRecords?: number;
}

export interface SyncConfirmationData {
  recordsReceived: number;
  timestamp: number;
}

export interface DatabaseRecord {
  id: string;
  data: Record<string, unknown>;
}

// A record deleted on the sending device. `rootTable`/`rootId` name the delete
// it belongs to: the record itself, or the post (count, factiii, upload) whose
// delete took this record with it.
export interface SyncTombstone {
  table: string;
  id: string;
  deletedAt: number;
  rootTable: string;
  rootId: string;
}

// A device's tombstones, in as many messages as they need. The receiver acts
// on them only once it holds the whole set (`last`), because a delete is
// decided as a whole and its tombstones may be split across messages.
export interface SyncTombstonesData {
  tombstones: SyncTombstone[];
  last: boolean;
}

// Blob sync types
export interface BlobData {
  key: string;
  uploadId: string;
  blobData: string;
  mimeType: string;
}

export interface BlobChunkData {
  key: string;
  uploadId: string;
  chunkIndex: number;
  totalChunks: number;
  chunkData: string;
  mimeType: string;
  totalSize: number;
}

export interface BlobChunkCompleteData {
  key: string;
  uploadId: string;
  totalChunks: number;
  totalSize: number;
}

// Database table names
export const SYNC_TABLES = {
  PRIVATE_POSTS: 'private_posts',
  PRIVATE_UPLOADS: 'private_uploads',
  PRIVATE_POST_FACTIII_COUNTS: 'private_post_factiii_counts',
  PRIVATE_POST_FACTIIIS: 'private_post_factiiis',
  PRIVATE_POST_FACTIII_UPLOADS: 'private_post_factiii_uploads',
  SPACE_TIMES: 'space_times',
  POST_ENGAGEMENTS: 'post_engagements',
  FACTIII_ENGAGEMENTS: 'factiii_engagements',
  MOOD_ENTRIES: 'mood_entries'
} as const;

export type SyncTableName = (typeof SYNC_TABLES)[keyof typeof SYNC_TABLES];

// Default sync order (respects foreign key dependencies)
export const DEFAULT_SYNC_ORDER: SyncTableName[] = [
  SYNC_TABLES.PRIVATE_POSTS,
  SYNC_TABLES.PRIVATE_UPLOADS,
  SYNC_TABLES.PRIVATE_POST_FACTIII_COUNTS,
  SYNC_TABLES.PRIVATE_POST_FACTIIIS,
  SYNC_TABLES.SPACE_TIMES,
  SYNC_TABLES.PRIVATE_POST_FACTIII_UPLOADS,
  SYNC_TABLES.POST_ENGAGEMENTS,
  SYNC_TABLES.FACTIII_ENGAGEMENTS,
  SYNC_TABLES.MOOD_ENTRIES
];
